// /app/frontend/src/pages/compliance/breach/constants.js
import { 
  FileText, Shield, Search, Users, ShieldAlert, Gauge, Send, Wrench, ClipboardCheck,
  Unlock, Ban
} from 'lucide-react';

// Step Configuration
export const STEPS = [
  { id: 1, label: 'Initial Report', icon: FileText },
  { id: 2, label: 'Classification', icon: Shield },
  { id: 3, label: 'Cause', icon: Search },
  { id: 4, label: 'Data Affected', icon: Users },
  { id: 5, label: 'Response', icon: ShieldAlert },
  { id: 6, label: 'Risk Assessment', icon: Gauge },
  { id: 7, label: 'Notifications', icon: Send },
  { id: 8, label: 'Remediation', icon: Wrench },
  { id: 9, label: 'Review', icon: ClipboardCheck },
];

export const BREACH_TYPES = [
  { id: 'confidentiality', label: 'Confidentiality', subtitle: 'Unauthorised or accidental disclosure of, or access to, personal data', icon: Unlock },
  { id: 'integrity', label: 'Integrity', subtitle: 'Unauthorised or accidental alteration of personal data', icon: Shield },
  { id: 'availability', label: 'Availability', subtitle: 'Unauthorised or accidental loss of access to, or destruction of, personal data', icon: Ban },
];

export const ORIGIN_OPTIONS = [
  'Human Error', 'Process Failure', 'System Failure', 'Malicious Internal',
  'Malicious External', 'Theft/Loss', 'Third Party', 'Unknown',
];

export const ROOT_CAUSE_CATEGORIES = [
  { name: 'Human Error', color: 'bg-orange-500', items: ['Email to wrong recipient', 'Accidental disclosure', 'Improper disposal', 'Misconfiguration', 'Lost device'] },
  { name: 'Physical Security', color: 'bg-blue-500', items: ['Unauthorised access', 'Stolen equipment', 'Tailgating', 'Improper document handling', 'Environmental damage'] },
  { name: 'Cyber Incident', color: 'bg-red-500', items: ['Phishing/social engineering', 'Ransomware/malware', 'Unauthorised system access', 'DDoS'] },
  { name: 'Technical', color: 'bg-purple-500', items: ['Software vulnerability', 'System failure/outage'] },
  { name: 'Malicious Action', color: 'bg-pink-500', items: ['Deliberate insider action'] },
  { name: 'Other', color: 'bg-gray-500', items: ['Other/not yet determined'] },
];

// Point-Based Scoring: ENISA DPC methodology + EDPB Guidelines 9/2022
export const SCORED_DATA_CATEGORIES = [
  {
    name: 'Sensitive / Special Category',
    color: 'bg-red-500',
    items: [
      { name: 'Health Data', points: 10, feeds: ['data_sensitivity'] },
      { name: 'Genetic Data', points: 10, feeds: ['data_sensitivity'] },
      { name: 'Biometric Data', points: 10, feeds: ['data_sensitivity'] },
      { name: 'Criminal Records', points: 8, feeds: ['data_sensitivity'] },
      { name: 'Racial / Ethnic Origin', points: 7, feeds: ['data_sensitivity'] },
      { name: 'Political Opinions', points: 7, feeds: ['data_sensitivity'] },
      { name: 'Religious / Philosophical Beliefs', points: 7, feeds: ['data_sensitivity'] },
      { name: 'Sexual Orientation / Life', points: 7, feeds: ['data_sensitivity'] },
      { name: 'Trade Union Membership', points: 5, feeds: ['data_sensitivity'] },
    ]
  },
  {
    name: 'Financial Data',
    color: 'bg-amber-500',
    items: [
      { name: 'Bank Account Details', points: 9, feeds: ['data_sensitivity', 'vulnerability'] },
      { name: 'Credit Card Numbers', points: 9, feeds: ['data_sensitivity', 'vulnerability'] },
      { name: 'Tax Records', points: 7, feeds: ['data_sensitivity'] },
      { name: 'Salary / Income', points: 6, feeds: ['data_sensitivity'] },
      { name: 'Insurance Information', points: 5, feeds: ['data_sensitivity'] },
      { name: 'Investment Records', points: 5, feeds: ['data_sensitivity'] },
    ]
  },
  {
    name: 'Identity Documents',
    color: 'bg-blue-500',
    items: [
      { name: 'National ID Number', points: 8, feeds: ['data_sensitivity', 'identification'] },
      { name: 'Passport', points: 8, feeds: ['data_sensitivity', 'identification'] },
      { name: "Driver's Licence", points: 7, feeds: ['data_sensitivity', 'identification'] },
      { name: 'Social Security Number', points: 8, feeds: ['data_sensitivity', 'identification'] },
      { name: 'Birth Certificate', points: 6, feeds: ['data_sensitivity', 'identification'] },
      { name: 'Immigration Documents', points: 7, feeds: ['data_sensitivity', 'identification'] },
    ]
  },
  {
    name: 'Digital & Authentication',
    color: 'bg-purple-500',
    items: [
      { name: 'Authentication Credentials', points: 8, feeds: ['vulnerability'] },
      { name: 'Login Credentials', points: 7, feeds: ['vulnerability'] },
      { name: 'Location / Movement Data', points: 4, feeds: ['data_sensitivity'] },
      { name: 'IP Address', points: 3, feeds: ['identification'] },
      { name: 'Online Identifiers', points: 3, feeds: ['identification'] },
      { name: 'Social Media Profiles', points: 3, feeds: ['identification'] },
    ]
  },
  {
    name: 'Contact Information',
    color: 'bg-green-500',
    items: [
      { name: 'Full Name', points: 3, feeds: ['identification'] },
      { name: 'Home Address', points: 4, feeds: ['identification', 'data_sensitivity'] },
      { name: 'Phone Number', points: 3, feeds: ['identification'] },
      { name: 'Date of Birth', points: 4, feeds: ['identification'] },
      { name: 'Employment Details', points: 3, feeds: ['data_sensitivity'] },
      { name: 'Education Records', points: 2, feeds: ['data_sensitivity'] },
      { name: 'Family / Relationship Data', points: 3, feeds: ['data_sensitivity'] },
    ]
  },
];

// Impact Areas (Step 6) — GDPR Recitals 75/85
export const IMPACT_AREAS = [
  {
    name: 'Physical Damage',
    color: 'bg-red-600',
    items: [
      { name: 'Stalking / tracking enabled', points: 10, feeds: ['vulnerability'] },
      { name: 'Domestic violence facilitation', points: 10, feeds: ['vulnerability'] },
      { name: 'Medical harm from data alteration/disclosure', points: 9, feeds: ['vulnerability'] },
      { name: 'Physical safety threat', points: 9, feeds: ['vulnerability'] },
      { name: 'Harm to vulnerable individuals', points: 10, feeds: ['vulnerability'] },
    ]
  },
  {
    name: 'Material / Economic Damage',
    color: 'bg-amber-600',
    items: [
      { name: 'Identity theft or fraud', points: 8, feeds: ['vulnerability', 'data_sensitivity'] },
      { name: 'Financial loss (direct)', points: 7, feeds: ['vulnerability'] },
      { name: 'Credit damage / blacklisting', points: 7, feeds: ['vulnerability'] },
      { name: 'Employment loss / discrimination', points: 8, feeds: ['vulnerability'] },
      { name: 'Insurance discrimination / denial', points: 5, feeds: ['vulnerability'] },
      { name: 'Denial of services or benefits', points: 5, feeds: ['vulnerability'] },
      { name: 'Property damage', points: 5, feeds: ['vulnerability'] },
      { name: 'Extra costs incurred', points: 3, feeds: ['vulnerability'] },
    ]
  },
  {
    name: 'Non-Material / Psychological',
    color: 'bg-violet-500',
    items: [
      { name: 'Loss of control over personal data', points: 2, feeds: ['data_sensitivity'] },
      { name: 'Limitation of rights', points: 4, feeds: ['vulnerability'] },
      { name: 'Discrimination', points: 7, feeds: ['vulnerability'] },
      { name: 'Damage to reputation', points: 5, feeds: ['vulnerability'] },
      { name: 'Significant humiliation', points: 6, feeds: ['vulnerability'] },
      { name: 'Emotional distress / anxiety', points: 4, feeds: ['vulnerability'] },
      { name: 'Fear / stress', points: 3, feeds: ['vulnerability'] },
      { name: 'Loss of confidentiality (professional secrecy)', points: 5, feeds: ['vulnerability', 'data_sensitivity'] },
      { name: 'Relationship damage', points: 4, feeds: ['vulnerability'] },
      { name: 'Social disadvantage / exclusion', points: 4, feeds: ['vulnerability'] },
    ]
  },
  {
    name: 'Special Circumstances',
    color: 'bg-pink-500',
    items: [
      { name: "Breach involves children's data", points: 10, feeds: ['vulnerability'] },
      { name: 'Breach involves vulnerable adults', points: 8, feeds: ['vulnerability'] },
      { name: 'Breach involves health data', points: 7, feeds: ['data_sensitivity'] },
      { name: 'Breach involves financial/credit data', points: 6, feeds: ['data_sensitivity'] },
      { name: 'Breach involves location data', points: 5, feeds: ['data_sensitivity'] },
      { name: 'Breach involves authentication credentials', points: 7, feeds: ['vulnerability'] },
      { name: 'Breach involves biometric/genetic data', points: 8, feeds: ['data_sensitivity'] },
    ]
  },
  {
    name: 'Rights Affected',
    color: 'bg-indigo-500',
    items: [
      { name: 'Right to private life', points: 5, feeds: ['vulnerability'] },
      { name: 'Right to family life', points: 4, feeds: ['vulnerability'] },
      { name: 'Freedom of expression (chilling effect)', points: 5, feeds: ['vulnerability'] },
      { name: 'Freedom of association', points: 4, feeds: ['vulnerability'] },
      { name: 'Freedom of movement', points: 4, feeds: ['vulnerability'] },
      { name: 'Right to non-discrimination', points: 5, feeds: ['vulnerability'] },
      { name: 'Right to dignity', points: 5, feeds: ['vulnerability'] },
    ]
  },
];

export const WHO_AFFECTED_OPTIONS = [
  'Employees', 'Former Employees', 'Job Applicants', 'Contractors',
  'Customers', 'Former Customers', 'Prospective Customers',
  'Suppliers/Vendors', 'Business Contacts', 'Website Visitors',
  'Members/Subscribers', 'Patients', 'Students', 'Minors (Under 18)',
  'Vulnerable Adults', 'General Public', 'Other',
];

export const CONTAINMENT_MEASURES = [
  'Affected systems isolated', 'Compromised accounts disabled', 'Passwords reset',
  'Access permissions reviewed', 'Security patches applied', 'Affected data recovered/restored',
  'Third parties notified', 'Evidence preserved', 'Remote wipe executed',
  'Encryption status verified', 'Enhanced monitoring deployed',
];

export const REMEDIATION_OPTIONS = [
  'Staff training', 'Policy update', 'Process improvement', 'Technical controls',
  'Access controls strengthened', 'Encryption implemented', 'Vendor review/audit',
  'Incident response plan updated',
];

export const SUPERVISORY_AUTHORITIES = [
  { value: 'oic_jm', label: 'Office of the Information Commissioner (Jamaica)' },
  { value: 'dpo_bb', label: 'Data Protection Office (Barbados)' },
  { value: 'odpa_ky', label: 'Office of the Data Protection Authority (Cayman Islands)' },
  { value: 'dpc_bs', label: 'Data Protection Commissioner (Bahamas)' },
  { value: 'pipa_bm', label: 'Privacy Commissioner (Bermuda)' },
  { value: 'oic_bz', label: 'Office of the Information Commissioner (Belize)' },
  { value: 'oic_tt', label: 'Office of the Information Commissioner (Trinidad and Tobago)' },
  { value: 'other', label: 'Other' },
];

// Caribbean Jurisdiction Breach Notification Rules — based on local data protection legislation
// dpo_discretion: false = mandatory notification (Jamaica, Cayman) — Internal/Not-a-breach options disabled
// dpo_discretion: true  = DPO assesses risk before deciding
export const JURISDICTION_BREACH_RULES = {
  'Jamaica': {
    code: 'JM',
    authority: 'oic_jm',
    authorityName: 'Office of the Information Commissioner',
    deadlineDays: 3,
    deadlineHours: 72,
    dpo_discretion: false,
    threshold: 'all',
    thresholdDescription: 'All personal data breaches must be reported',
    legalBasis: 'Data Protection Act 2020, Section 21',
    subjectNotification: 'Required for all breaches',
    regulatorLink: 'https://www.oic.org.jm',
  },
  'Barbados': {
    code: 'BB',
    authority: 'dpo_bb',
    authorityName: 'Data Protection Commissioner',
    deadlineDays: 3,
    deadlineHours: 72,
    dpo_discretion: true,
    threshold: 'high_risk',
    thresholdDescription: 'Report only if likely to result in high risk to rights and freedoms of data subjects',
    legalBasis: 'Data Protection Act 2019, Section 63',
    subjectNotification: 'Required if high risk to rights and freedoms',
    regulatorLink: 'https://www.dpo.gov.bb',
  },
  'Cayman Islands': {
    code: 'KY',
    authority: 'ombudsman_ky',
    authorityName: 'Office of the Ombudsman',
    deadlineDays: 5,
    deadlineHours: 120,
    dpo_discretion: false,
    threshold: 'all',
    thresholdDescription: 'All personal data breaches must be reported',
    legalBasis: 'Data Protection Act (2021 Revision), Section 16',
    subjectNotification: 'Required for all breaches (simultaneous with regulator notification)',
    regulatorLink: 'https://www.ombudsman.ky',
  },
  'Bahamas': {
    code: 'BS',
    authority: 'dpc_bs',
    authorityName: 'Data Protection Commissioner',
    deadlineDays: 3,
    deadlineHours: 72,
    dpo_discretion: true,
    threshold: 'high_risk',
    thresholdDescription: 'Report only if likely to result in risk to the fundamental rights and freedoms of an identifiable natural person',
    legalBasis: 'Data Protection Bill 2025, Section 47',
    subjectNotification: 'Required if likely to result in violation of fundamental rights and freedoms of data subject',
    regulatorLink: 'https://www.dpcommissioner.bs',
  },
  'Bermuda': {
    code: 'BM',
    authority: 'pipa_bm',
    authorityName: 'Privacy Commissioner',
    deadlineDays: 3,
    deadlineHours: 72,
    dpo_discretion: true,
    threshold: 'high_risk',
    thresholdDescription: 'Report only if breach is likely to adversely affect an individual',
    legalBasis: 'Personal Information Protection Act 2016, Section 14',
    subjectNotification: 'Required if significant harm likely',
    regulatorLink: 'https://www.privacycommissioner.bm',
  },
  'Belize': {
    code: 'BZ',
    authority: 'oic_bz',
    authorityName: 'Information Commissioner',
    deadlineDays: 3,
    deadlineHours: 72,
    dpo_discretion: true,
    threshold: 'high_risk',
    thresholdDescription: 'Report only if likely to result in high risk to rights and freedoms of data subjects',
    legalBasis: 'Data Protection Act, 2021',
    subjectNotification: 'Required if high risk to rights and freedoms',
    regulatorLink: null,
  },
  'Trinidad and Tobago': {
    code: 'TT',
    authority: 'oic_tt',
    authorityName: 'Office of the Information Commissioner',
    deadlineDays: 3,
    deadlineHours: 72,
    dpo_discretion: true,
    threshold: 'high_risk',
    thresholdDescription: 'Report only if likely to result in high risk to rights and freedoms of data subjects',
    legalBasis: 'Data Protection Chap. 22:04',
    subjectNotification: 'Required if high risk to rights and freedoms',
    regulatorLink: 'http://laws.gov.tt/ttdll-web/revision/list?offset=0&q=data%20protection&currentid=80855',
  },
  'Other': {
    code: 'OTHER',
    authority: 'other',
    authorityName: 'Supervisory Authority',
    deadlineDays: 3,
    deadlineHours: 72,
    dpo_discretion: true,
    threshold: 'high_risk',
    thresholdDescription: 'DPO to determine based on applicable law',
    legalBasis: 'Applicable data protection legislation',
    subjectNotification: 'As required by applicable law',
    regulatorLink: null,
  },
};

// Temporary bridge — maps entity.jurisdiction / entity.country free-text to JURISDICTION_BREACH_RULES key.
// Will be removed when entity jurisdiction field is migrated to ISO codes.
const JURISDICTION_NAME_MAP = {
  'Jamaica': 'Jamaica',
  'Barbados': 'Barbados',
  'Trinidad and Tobago': 'Trinidad and Tobago',
  'Trinidad': 'Trinidad and Tobago',
  'Cayman Islands': 'Cayman Islands',
  'Cayman': 'Cayman Islands',
  'Bahamas': 'Bahamas',
  'Bermuda': 'Bermuda',
  'Belize': 'Belize',
};

export function getJurisdictionRules(entityJurisdiction) {
  if (!entityJurisdiction) return null;
  const key = JURISDICTION_NAME_MAP[entityJurisdiction];
  if (!key) return null;
  const rules = JURISDICTION_BREACH_RULES[key];
  return rules ? { ...rules, name: key } : null;
}

// Helper function to get notification requirement based on jurisdiction and risk level
export const getNotificationRequirement = (jurisdiction, riskLevel) => {
  const rules = JURISDICTION_BREACH_RULES[jurisdiction];
  if (!rules) return null;
  
  const isHighRisk = riskLevel === 'High' || riskLevel === 'Critical';
  const isMediumOrAbove = isHighRisk || riskLevel === 'Medium';
  
  let mustReport = false;
  let reportReason = '';
  
  switch (rules.threshold) {
    case 'all':
      mustReport = true;
      reportReason = 'All breaches must be reported under ' + rules.legalBasis;
      break;
    case 'high_risk':
      mustReport = isHighRisk;
      reportReason = isHighRisk 
        ? 'High risk breach - must report under ' + rules.legalBasis
        : 'Not high risk - reporting not mandatory, but recommended to document decision';
      break;
    case 'dpo_discretion':
      mustReport = null; // DPO decides
      reportReason = 'DPO to assess reporting requirement based on circumstances';
      break;
    default:
      mustReport = null;
      reportReason = 'Unable to determine - DPO to assess';
  }
  
  return {
    ...rules,
    mustReport,
    reportReason,
    isHighRisk,
    suggestedDeadline: rules.deadlineDays,
  };
};

export const SEVERITY_LABELS = ['Negligible', 'Minor', 'Moderate', 'Major', 'Severe'];
export const LIKELIHOOD_LABELS = ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'];

export const MATRIX_WEIGHTS = {
  severity: { data_sensitivity: 0.43, identification: 0.29, vulnerability: 0.28 },
  likelihood: { volume: 0.50, containment: 0.50 },
};

export const RISK_BANDS = [
  { max: 4, level: 'low', color: '#059669', action: 'Internal record only — document breach and assessment', ref: 'Art. 33(1) exception' },
  { max: 9, level: 'moderate', color: '#D97706', action: 'Notify supervisory authority; consider data subject notification', ref: 'Art. 33(1): notify DPA within 72 hours' },
  { max: 15, level: 'high', color: '#DC2626', action: 'Notify supervisory authority and data subjects', ref: 'Art. 33 + Art. 34' },
  { max: 25, level: 'extreme', color: '#991B1B', action: 'Immediate action — notify authority, data subjects, and escalate', ref: 'Art. 33 + Art. 34 + immediate escalation' },
];

export const getRiskBand = (score) => RISK_BANDS.find(b => score <= b.max) || RISK_BANDS[3];
export const rawToLevel = (raw) => Math.min(5, Math.max(1, Math.ceil(raw / 20)));

export const INITIAL_FORM_DATA = {
  entity_id: '',
  is_personal_data_breach: null,
  incident_or_breach: 'unknown',
  incident_notes: '',
  incident_closed_date: '',
  incident_closure_reason: '',
  breach_reference: '',
  title: '',
  awareness_date: '',
  breach_date: '',
  what_happened: '',
  breach_types: [],
  origin: '',
  systems_affected: [],
  root_causes: [],
  data_categories: [],
  who_affected: [],
  number_affected: 0,
  number_affected_estimate: false,
  actions_taken: '',
  containment_measures: [],
  containment_measures_dates: {},
  containment_date: '',
  incident_owner: '',
  risk_input_values: [50, 50, 50, 50, 50],
  risk_score: null,
  risk_level: null,
  risk_label: null,
  risk_action: null,
  risk_factors: null,
  impact_areas: [],
  auto_severity: null,
  auto_likelihood: null,
  auto_matrix_score: null,
  override_severity: null,
  override_likelihood: null,
  override_justification: '',
  override_by: null,
  override_at: null,
  final_risk_level: null,
  points_breakdown: null,
  // Jurisdiction-aware notification fields
  jurisdiction: '',
  jurisdiction_code: '',
  supervisory_authority: '',
  notification_required: null,
  notification_requirement_reason: '',
  notification_reason: '',
  suggested_deadline_days: null,
  confirmed_deadline: '',
  deadline_at: null,
  dpo_discretion_applied: null,
  regulator_notified_at: null,
  subject_notified_at: null,
  dpo_reporting_decision: 'pending', // 'report', 'no_report', 'pending', 'not_a_breach'
  dpo_reporting_justification: '',
  notification_deadline: '',
  date_notified: '',
  reference_number: '',
  subject_notification: '',
  subject_notification_required: null,
  communication_method: '',
  preventive_measures: '',
  remediation_measures: [],
  remediation_actions: [],
  target_completion_date: '',
  assigned_to: '',
  dpo_signoff_name: '',
  dpo_signoff_date: '',
  dpo_signoff_confirmed: false,
  current_step: 1,
  status: 'draft',
  regulator_deadline: '',
};

// Scoring Logic
export const ALL_SCOREABLE = [...SCORED_DATA_CATEGORIES, ...IMPACT_AREAS];

export function computeMaxPoints(allCategories) {
  const maxPts = { data_sensitivity: 0, identification: 0, vulnerability: 0 };
  allCategories.forEach(cat => cat.items.forEach(item => {
    item.feeds.forEach(feed => { if (maxPts[feed] !== undefined) maxPts[feed] += item.points; });
  }));
  return maxPts;
}

export const MAX_POINTS = computeMaxPoints(ALL_SCOREABLE);

export function computePointsBreakdown(selectedDataCats, selectedImpacts) {
  const pts = { data_sensitivity: 0, identification: 0, vulnerability: 0 };
  const allSelected = new Set([...selectedDataCats, ...selectedImpacts]);
  ALL_SCOREABLE.forEach(cat => cat.items.forEach(item => {
    if (allSelected.has(item.name)) {
      item.feeds.forEach(feed => { if (pts[feed] !== undefined) pts[feed] += item.points; });
    }
  }));
  return pts;
}

export function normalisePoints(pts) {
  return {
    data_sensitivity: Math.min(100, Math.round((pts.data_sensitivity / MAX_POINTS.data_sensitivity) * 100)),
    identification: Math.min(100, Math.round((pts.identification / MAX_POINTS.identification) * 100)),
    vulnerability: Math.min(100, Math.round((pts.vulnerability / MAX_POINTS.vulnerability) * 100)),
  };
}

export function volumeScore(numAffected) {
  if (!numAffected || numAffected <= 0) return 0;
  return Math.min(100, Math.round((Math.log(numAffected + 1) / Math.log(100001)) * 100));
}

export function containmentScore(measuresChecked) {
  return Math.round(100 - ((measuresChecked / 11) * 100));
}
